import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAllOrganizers, updateUser, createUserDoc } from '../../services/api';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import toast from 'react-hot-toast';

const ManageOrganizers = () => {
    const queryClient = useQueryClient();
    const { data: organizers = [], isLoading } = useQuery({
        queryKey: ['admin-all-organizers'],
        queryFn: getAllOrganizers
    });

    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);

    // Form states
    const [formData, setFormData] = useState({});

    // Mutations
    const updateMutation = useMutation({
        mutationFn: ({ userId, data }) => updateUser(userId, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['admin-all-organizers']);
            toast.success('Organizador actualizado');
            setIsEditModalOpen(false);
        },
        onError: () => toast.error('Error al actualizar')
    });

    const createMutation = useMutation({
        mutationFn: (data) => createUserDoc(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['admin-all-organizers']);
            toast.success('Organizador creado (Firestore)');
            setIsCreateModalOpen(false);
        },
        onError: () => toast.error('Error al crear')
    });

    // Sorting Logic
    const sortedOrganizers = useMemo(() => {
        let sortableItems = [...organizers];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                // Safety check for null values
                const aValue = a[sortConfig.key] || '';
                const bValue = b[sortConfig.key] || '';

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [organizers, sortConfig]);

    // Filtering Logic
    const filteredOrganizers = sortedOrganizers.filter(org => 
        org.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    // Modal Handlers
    const openEditModal = (user) => {
        setCurrentUser(user);
        setFormData({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            role: user.role || 'organizer',
            disabled: user.disabled || false,
            phone: user.phone || '',
            gender: user.gender || '',
            level: user.level || 0.5,
            laterality: user.laterality || '',
            position: user.position || '',
            matchesPlayed: user.matchesPlayed || 0
        });
        setIsEditModalOpen(true);
    };

    const openCreateModal = () => {
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            role: 'organizer',
            disabled: false,
            phone: '',
            gender: '',
            level: 0.5,
            laterality: '',
            position: '',
            matchesPlayed: 0
        });
        setIsCreateModalOpen(true);
    };

    const handleUpdate = (e) => {
        e.preventDefault();
        updateMutation.mutate({ userId: currentUser.id, data: formData });
    };

    const handleCreate = (e) => {
        e.preventDefault();
        createMutation.mutate(formData);
    };

    // Helper for Sort Icon
    const getClassNamesFor = (name) => {
        if (!sortConfig) return;
        return sortConfig.key === name ? sortConfig.direction : undefined;
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tighter italic">Gestión de Organizadores</h1>
                    <p className="text-slate-500 font-medium italic">Control de cuentas profesionales y partners del SaaS</p>
                </div>
                <button 
                    onClick={openCreateModal}
                    className="bg-slate-900 hover:bg-slate-800 text-primary-400 font-bold py-2 px-4 rounded-xl transition-all shadow-lg flex items-center gap-2"
                >
                    + Nuevo Organizador
                </button>
            </div>

            {/* Search Bar */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex gap-4">
                <input 
                    type="text" 
                    placeholder="Buscar por nombre o email..." 
                    className="flex-1 border-none bg-slate-50 rounded-xl px-4 py-2 focus:ring-2 focus:ring-primary-400 outline-none"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th 
                                    onClick={() => requestSort('firstName')}
                                    className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] cursor-pointer hover:text-slate-700 select-none"
                                >
                                    Nombre {getClassNamesFor('firstName') === 'ascending' ? '↑' : getClassNamesFor('firstName') === 'descending' ? '↓' : ''}
                                </th>
                                <th 
                                    onClick={() => requestSort('email')}
                                    className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] cursor-pointer hover:text-slate-700 select-none"
                                >
                                    Email {getClassNamesFor('email') === 'ascending' ? '↑' : getClassNamesFor('email') === 'descending' ? '↓' : ''}
                                </th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Estado</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Acceso</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {filteredOrganizers.map((org) => (
                                <tr key={org.id} className={`hover:bg-slate-50/50 transition-colors group ${org.disabled ? 'bg-red-50/30' : ''}`}>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs uppercase shadow-sm ${org.disabled ? 'bg-red-100 text-red-500' : 'bg-slate-900 text-primary-400'}`}>
                                                {org.firstName?.charAt(0)}
                                            </div>
                                            <p className={`font-bold text-sm italic ${org.disabled ? 'text-red-400 line-through' : 'text-slate-900'}`}>
                                                {org.firstName} {org.lastName}
                                            </p>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="text-xs text-slate-500 font-medium italic">{org.email}</p>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 text-[8px] font-black rounded-full uppercase tracking-tighter ${org.disabled ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'}`}>
                                            {org.disabled ? 'BLOQUEADO' : 'ACTIVO'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="px-2 py-1 bg-slate-100 text-slate-600 text-[8px] font-black rounded-full uppercase tracking-widest">
                                            {org.role}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <button 
                                                onClick={() => openEditModal(org)}
                                                className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
                                                title="Editar perfil"
                                            >
                                                ✏️
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit User Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-lg p-8 shadow-2xl">
                        <h2 className="text-2xl font-black text-slate-900 mb-6 font-display italic">Editar Organizador</h2>
                        <form onSubmit={handleUpdate} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Nombre</label>
                                    <input 
                                        type="text" 
                                        value={formData.firstName}
                                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Apellido</label>
                                    <input 
                                        type="text" 
                                        value={formData.lastName}
                                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Email</label>
                                <input 
                                    type="email" 
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400"
                                />
                            </div>

                            {/* Datos Deportivo y Contacto Extra */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Teléfono</label>
                                    <input 
                                        type="tel" 
                                        value={formData.phone}
                                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400"
                                        placeholder="+34..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Partidos Jugados</label>
                                    <input 
                                        type="number" 
                                        value={formData.matchesPlayed}
                                        onChange={(e) => setFormData({...formData, matchesPlayed: Number(e.target.value)})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Género</label>
                                    <select 
                                        value={formData.gender}
                                        onChange={(e) => setFormData({...formData, gender: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-2 py-3 focus:ring-2 focus:ring-primary-400 text-sm"
                                    >
                                        <option value="">-</option>
                                        <option value="male">Hombre</option>
                                        <option value="female">Mujer</option>
                                        <option value="other">Otro</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Mano</label>
                                    <select 
                                        value={formData.laterality}
                                        onChange={(e) => setFormData({...formData, laterality: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-2 py-3 focus:ring-2 focus:ring-primary-400 text-sm"
                                    >
                                        <option value="">-</option>
                                        <option value="right">Diestro</option>
                                        <option value="left">Zurdo</option>
                                        <option value="ambidextrous">Ambos</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Posición</label>
                                    <select 
                                        value={formData.position}
                                        onChange={(e) => setFormData({...formData, position: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-2 py-3 focus:ring-2 focus:ring-primary-400 text-sm"
                                    >
                                        <option value="">-</option>
                                        <option value="drive">Drive</option>
                                        <option value="reves">Revés</option>
                                        <option value="both">Indiferente</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div>
                                <div className="flex justify-between mb-1">
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">Nivel: {formData.level?.toFixed(1)}</label>
                                </div>
                                <input 
                                    type="range" 
                                    min="0.5" 
                                    max="7.0" 
                                    step="0.1"
                                    value={formData.level}
                                    onChange={(e) => setFormData({...formData, level: parseFloat(e.target.value)})}
                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
                                />
                                <div className="flex justify-between text-[10px] text-slate-400 px-1 mt-1">
                                    <span>0.5 (Principiante)</span>
                                    <span>7.0 (Pro)</span>
                                </div>
                            </div>

                            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <div className="relative inline-flex items-center">
                                        <input 
                                            type="checkbox" 
                                            checked={formData.disabled} 
                                            onChange={(e) => setFormData({...formData, disabled: e.target.checked})}
                                            className="sr-only peer" 
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                                    </div>
                                    <span className="text-sm font-bold text-slate-700">Bloquear Acceso al Sistema</span>
                                </label>
                                <p className="text-xs text-slate-400 mt-2 pl-14">
                                    Si se activa, el usuario no podrá iniciar sesión ni acceder a la plataforma.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button 
                                    type="button"
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="px-6 py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-100 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit"
                                    className="px-6 py-3 rounded-xl font-bold bg-primary-500 text-white hover:bg-primary-600 shadow-lg shadow-primary-500/30 transition-all"
                                >
                                    Guardar Cambios
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create User Modal */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-lg p-8 shadow-2xl">
                        <h2 className="text-2xl font-black text-slate-900 mb-6 font-display italic">Nuevo Organizador (Manual)</h2>
                        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6 text-sm text-amber-800">
                            Pre-registro en base de datos. El usuario deberá registrarse normalmente o tendrás que gestionar su auth manualmente.
                        </div>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Nombre</label>
                                    <input 
                                        type="text" 
                                        required
                                        value={formData.firstName}
                                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Apellido</label>
                                    <input 
                                        type="text" 
                                        required
                                        value={formData.lastName}
                                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Email</label>
                                <input 
                                    type="email" 
                                    required
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400"
                                />
                            </div>

                             {/* Datos Deportivo y Contacto Extra (Create) */}
                             <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Teléfono</label>
                                    <input 
                                        type="tel" 
                                        value={formData.phone}
                                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400"
                                        placeholder="+34..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Partidos Jugados</label>
                                    <input 
                                        type="number" 
                                        value={formData.matchesPlayed}
                                        onChange={(e) => setFormData({...formData, matchesPlayed: Number(e.target.value)})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-400"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Género</label>
                                    <select 
                                        value={formData.gender}
                                        onChange={(e) => setFormData({...formData, gender: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-2 py-3 focus:ring-2 focus:ring-primary-400 text-sm"
                                    >
                                        <option value="">-</option>
                                        <option value="male">Hombre</option>
                                        <option value="female">Mujer</option>
                                        <option value="other">Otro</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Mano</label>
                                    <select 
                                        value={formData.laterality}
                                        onChange={(e) => setFormData({...formData, laterality: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-2 py-3 focus:ring-2 focus:ring-primary-400 text-sm"
                                    >
                                        <option value="">-</option>
                                        <option value="right">Diestro</option>
                                        <option value="left">Zurdo</option>
                                        <option value="ambidextrous">Ambos</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Posición</label>
                                    <select 
                                        value={formData.position}
                                        onChange={(e) => setFormData({...formData, position: e.target.value})}
                                        className="w-full bg-slate-50 border-none rounded-xl px-2 py-3 focus:ring-2 focus:ring-primary-400 text-sm"
                                    >
                                        <option value="">-</option>
                                        <option value="drive">Drive</option>
                                        <option value="reves">Revés</option>
                                        <option value="both">Indiferente</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div>
                                <div className="flex justify-between mb-1">
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">Nivel: {formData.level?.toFixed(1)}</label>
                                </div>
                                <input 
                                    type="range" 
                                    min="0.5" 
                                    max="7.0" 
                                    step="0.1"
                                    value={formData.level}
                                    onChange={(e) => setFormData({...formData, level: parseFloat(e.target.value)})}
                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
                                />
                                <div className="flex justify-between text-[10px] text-slate-400 px-1 mt-1">
                                    <span>0.5 (Principiante)</span>
                                    <span>7.0 (Pro)</span>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4">
                                <button 
                                    type="button"
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="px-6 py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-100 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit"
                                    className="px-6 py-3 rounded-xl font-bold bg-slate-900 text-primary-400 hover:bg-slate-800 shadow-lg transition-all"
                                >
                                    Crear Registro
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageOrganizers;
